import { useState } from 'react';
import MainLayout from '../../../../components/layout/MainLayout';
import CategorySearchFilters from './components/CategorySearchFilters';
import CategoryFormModal from './components/CategoryFormModal';
import ConfirmModal from '../components/ConfirmModal';
import { mockCategories } from '../mockData';
import type { AppCategory } from '../types';

export default function AppCategoriesPage() {
  const [categories, setCategories] = useState<AppCategory[]>(mockCategories);
  const [filteredCategories, setFilteredCategories] = useState<AppCategory[]>(mockCategories);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [formCategory, setFormCategory] = useState<AppCategory | undefined>(undefined);
  const [parentCategory, setParentCategory] = useState<AppCategory | undefined>(undefined);
  const [showFormModal, setShowFormModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type?: 'danger' | 'warning' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });

  const handleSearch = (keyword: string) => {
    let filtered = categories;
    if (keyword) {
      filtered = filtered.map(cat => ({
        ...cat,
        children: cat.children?.filter(sub =>
          sub.name.toLowerCase().includes(keyword.toLowerCase())
        )
      })).filter(cat =>
        cat.name.toLowerCase().includes(keyword.toLowerCase()) ||
        (cat.children && cat.children.length > 0)
      );
    }
    setFilteredCategories(filtered);
  };

  const handleStatusChange = (status: string) => {
    let filtered = categories;
    if (status) {
      const statusBool = status === 'true';
      filtered = filtered.filter(cat => cat.status === statusBool);
    }
    setFilteredCategories(filtered);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = filteredCategories.flatMap(cat => [
        cat.id,
        ...(cat.children?.map(sub => sub.id) || [])
      ]);
      setSelectedCategories(allIds);
    } else {
      setSelectedCategories([]);
    }
  };

  const handleSelectCategory = (categoryId: string) => {
    setSelectedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleBatchEnable = () => {
    setConfirmModal({
      isOpen: true,
      title: '批量启用',
      message: `确定要启用选中的 ${selectedCategories.length} 个分类吗？`,
      type: 'info',
      onConfirm: () => {
        setCategories(prev =>
          prev.map(cat => ({
            ...cat,
            status: selectedCategories.includes(cat.id) ? true : cat.status,
            children: cat.children?.map(sub => ({
              ...sub,
              status: selectedCategories.includes(sub.id) ? true : sub.status
            }))
          }))
        );
        setFilteredCategories(prev =>
          prev.map(cat => ({
            ...cat,
            status: selectedCategories.includes(cat.id) ? true : cat.status,
            children: cat.children?.map(sub => ({
              ...sub,
              status: selectedCategories.includes(sub.id) ? true : sub.status
            }))
          }))
        );
        setSelectedCategories([]);
        setConfirmModal({ ...confirmModal, isOpen: false });
      }
    });
  };

  const handleBatchDisable = () => {
    setConfirmModal({
      isOpen: true,
      title: '批量禁用',
      message: `确定要禁用选中的 ${selectedCategories.length} 个分类吗？`,
      type: 'warning',
      onConfirm: () => {
        setCategories(prev =>
          prev.map(cat => ({
            ...cat,
            status: selectedCategories.includes(cat.id) ? false : cat.status,
            children: cat.children?.map(sub => ({
              ...sub,
              status: selectedCategories.includes(sub.id) ? false : sub.status
            }))
          }))
        );
        setFilteredCategories(prev =>
          prev.map(cat => ({
            ...cat,
            status: selectedCategories.includes(cat.id) ? false : cat.status,
            children: cat.children?.map(sub => ({
              ...sub,
              status: selectedCategories.includes(sub.id) ? false : sub.status
            }))
          }))
        );
        setSelectedCategories([]);
        setConfirmModal({ ...confirmModal, isOpen: false });
      }
    });
  };

  const handleBatchDelete = () => {
    setConfirmModal({
      isOpen: true,
      title: '批量删除',
      message: `确定要删除选中的 ${selectedCategories.length} 个分类吗？此操作不可恢复。`,
      type: 'danger',
      onConfirm: () => {
        setCategories(prev =>
          prev
            .filter(cat => !selectedCategories.includes(cat.id))
            .map(cat => ({
              ...cat,
              children: cat.children?.filter(sub => !selectedCategories.includes(sub.id))
            }))
        );
        setFilteredCategories(prev =>
          prev
            .filter(cat => !selectedCategories.includes(cat.id))
            .map(cat => ({
              ...cat,
              children: cat.children?.filter(sub => !selectedCategories.includes(sub.id))
            }))
        );
        setSelectedCategories([]);
        setConfirmModal({ ...confirmModal, isOpen: false });
      }
    });
  };

  const handleToggleStatus = (categoryId: string, isParent: boolean) => {
    if (isParent) {
      setCategories(prev =>
        prev.map(cat =>
          cat.id === categoryId ? { ...cat, status: !cat.status } : cat
        )
      );
      setFilteredCategories(prev =>
        prev.map(cat =>
          cat.id === categoryId ? { ...cat, status: !cat.status } : cat
        )
      );
    } else {
      setCategories(prev =>
        prev.map(cat => ({
          ...cat,
          children: cat.children?.map(sub =>
            sub.id === categoryId ? { ...sub, status: !sub.status } : sub
          )
        }))
      );
      setFilteredCategories(prev =>
        prev.map(cat => ({
          ...cat,
          children: cat.children?.map(sub =>
            sub.id === categoryId ? { ...sub, status: !sub.status } : sub
          )
        }))
      );
    }
  };

  const handleUpdateSort = (categoryId: string, newSort: number, isParent: boolean) => {
    if (isParent) {
      setCategories(prev =>
        prev.map(cat =>
          cat.id === categoryId ? { ...cat, sort: newSort } : cat
        ).sort((a, b) => a.sort - b.sort)
      );
      setFilteredCategories(prev =>
        prev.map(cat =>
          cat.id === categoryId ? { ...cat, sort: newSort } : cat
        ).sort((a, b) => a.sort - b.sort)
      );
    } else {
      setCategories(prev =>
        prev.map(cat => ({
          ...cat,
          children: cat.children?.map(sub =>
            sub.id === categoryId ? { ...sub, sort: newSort } : sub
          ).sort((a, b) => a.sort - b.sort)
        }))
      );
      setFilteredCategories(prev =>
        prev.map(cat => ({
          ...cat,
          children: cat.children?.map(sub =>
            sub.id === categoryId ? { ...sub, sort: newSort } : sub
          ).sort((a, b) => a.sort - b.sort)
        }))
      );
    }
  };

  const handleDelete = (categoryId: string, isParent: boolean, hasProducts: boolean) => {
    if (hasProducts) {
      alert('该分类下存在关联应用，无法删除');
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: '删除分类',
      message: '确定要删除此分类吗？此操作不可恢复。',
      type: 'danger',
      onConfirm: () => {
        if (isParent) {
          setCategories(prev => prev.filter(cat => cat.id !== categoryId));
          setFilteredCategories(prev => prev.filter(cat => cat.id !== categoryId));
        } else {
          setCategories(prev =>
            prev.map(cat => ({
              ...cat,
              children: cat.children?.filter(sub => sub.id !== categoryId)
            }))
          );
          setFilteredCategories(prev =>
            prev.map(cat => ({
              ...cat,
              children: cat.children?.filter(sub => sub.id !== categoryId)
            }))
          );
        }
        setConfirmModal({ ...confirmModal, isOpen: false });
      }
    });
  };

  const handleSaveForm = (data: any) => {
    if (formCategory) {
      // 编辑
      if (formCategory.parentId) {
        setCategories(prev =>
          prev.map(cat => ({
            ...cat,
            children: cat.children?.map(sub =>
              sub.id === formCategory.id ? { ...sub, ...data } : sub
            )
          }))
        );
        setFilteredCategories(prev =>
          prev.map(cat => ({
            ...cat,
            children: cat.children?.map(sub =>
              sub.id === formCategory.id ? { ...sub, ...data } : sub
            )
          }))
        );
      } else {
        setCategories(prev =>
          prev.map(cat =>
            cat.id === formCategory.id ? { ...cat, ...data } : cat
          )
        );
        setFilteredCategories(prev =>
          prev.map(cat =>
            cat.id === formCategory.id ? { ...cat, ...data } : cat
          )
        );
      }
    } else {
      // 新建
      if (data.parentId) {
        const newSubCategory: AppCategory = {
          id: `${data.parentId}-SUB${String(Date.now()).slice(-3)}`,
          name: data.name,
          status: data.status,
          sort: data.sort,
          parentId: data.parentId,
          productCount: 0
        };
        setCategories(prev =>
          prev.map(cat =>
            cat.id === data.parentId
              ? { ...cat, children: [...(cat.children || []), newSubCategory] }
              : cat
          )
        );
        setFilteredCategories(prev =>
          prev.map(cat =>
            cat.id === data.parentId
              ? { ...cat, children: [...(cat.children || []), newSubCategory] }
              : cat
          )
        );
      } else {
        const newCategory: AppCategory = {
          id: `CAT${String(Date.now()).slice(-3)}`,
          name: data.name,
          status: data.status,
          sort: data.sort,
          productCount: 0,
          children: []
        };
        setCategories(prev => [...prev, newCategory]);
        setFilteredCategories(prev => [...prev, newCategory]);
      }
    }
    setShowFormModal(false);
    setFormCategory(undefined);
    setParentCategory(undefined);
  };

  return (
    <MainLayout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">应用分类管理</h1>
            <p className="text-sm text-gray-500 mt-1">管理应用市场的分类体系</p>
          </div>
          <button
            onClick={() => {
              setFormCategory(undefined);
              setParentCategory(undefined);
              setShowFormModal(true);
            }}
            className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium whitespace-nowrap"
          >
            <i className="ri-add-line mr-2"></i>
            新建分类
          </button>
        </div>

        <CategorySearchFilters onSearch={handleSearch} onStatusChange={handleStatusChange} />

        {selectedCategories.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-center justify-between">
            <div className="text-sm text-blue-800">
              已选择 <span className="font-semibold">{selectedCategories.length}</span> 个分类
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleBatchEnable}
                className="px-4 py-2 bg-white text-blue-700 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors text-sm font-medium whitespace-nowrap"
              >
                <i className="ri-check-line mr-2"></i>
                批量启用
              </button>
              <button
                onClick={handleBatchDisable}
                className="px-4 py-2 bg-white text-orange-700 border border-orange-300 rounded-lg hover:bg-orange-50 transition-colors text-sm font-medium whitespace-nowrap"
              >
                <i className="ri-close-line mr-2"></i>
                批量禁用
              </button>
              <button
                onClick={handleBatchDelete}
                className="px-4 py-2 bg-white text-red-700 border border-red-300 rounded-lg hover:bg-red-50 transition-colors text-sm font-medium whitespace-nowrap"
              >
                <i className="ri-delete-bin-line mr-2"></i>
                批量删除
              </button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left">
                    <input
                      type="checkbox"
                      checked={
                        selectedCategories.length ===
                        filteredCategories.flatMap(cat => [cat.id, ...(cat.children?.map(sub => sub.id) || [])]).length &&
                        filteredCategories.length > 0
                      }
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="w-4 h-4 text-teal-600 focus:ring-teal-500 rounded"
                    />
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                    排序
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                    分类ID
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                    分类名称
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                    应用数量
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                    分类状态
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredCategories.map((category) => (
                  <>
                    <tr key={category.id} className="hover:bg-gray-50 bg-blue-50/30">
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedCategories.includes(category.id)}
                          onChange={() => handleSelectCategory(category.id)}
                          className="w-4 h-4 text-teal-600 focus:ring-teal-500 rounded"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <input
                          type="number"
                          value={category.sort}
                          onChange={(e) => handleUpdateSort(category.id, parseInt(e.target.value) || 999, true)}
                          className="w-20 px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                        />
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap font-mono">
                        {category.id}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <i className="ri-folder-line text-teal-600"></i>
                          <span className="font-semibold text-gray-900">{category.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                        {category.productCount || 0}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleToggleStatus(category.id, true)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            category.status ? 'bg-teal-600' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              category.status ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => {
                              setParentCategory(category);
                              setFormCategory(undefined);
                              setShowFormModal(true);
                            }}
                            className="w-8 h-8 flex items-center justify-center text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="添加下级分类"
                          >
                            <i className="ri-add-line"></i>
                          </button>
                          <button
                            onClick={() => {
                              setFormCategory(category);
                              setParentCategory(undefined);
                              setShowFormModal(true);
                            }}
                            className="w-8 h-8 flex items-center justify-center text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                            title="编辑"
                          >
                            <i className="ri-edit-line"></i>
                          </button>
                          <button
                            onClick={() => handleDelete(category.id, true, (category.productCount || 0) > 0)}
                            className="w-8 h-8 flex items-center justify-center text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="删除"
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                    {category.children?.map((subCategory) => (
                      <tr key={subCategory.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <input
                            type="checkbox"
                            checked={selectedCategories.includes(subCategory.id)}
                            onChange={() => handleSelectCategory(subCategory.id)}
                            className="w-4 h-4 text-teal-600 focus:ring-teal-500 rounded"
                          />
                        </td>
                        <td className="px-6 py-4">
                          <input
                            type="number"
                            value={subCategory.sort}
                            onChange={(e) => handleUpdateSort(subCategory.id, parseInt(e.target.value) || 999, false)}
                            className="w-20 px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                          />
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap font-mono">
                          {subCategory.id}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2 pl-8">
                            <i className="ri-file-list-line text-gray-400"></i>
                            <span className="text-gray-900">{subCategory.name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                          {subCategory.productCount || 0}
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => handleToggleStatus(subCategory.id, false)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              subCategory.status ? 'bg-teal-600' : 'bg-gray-300'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                subCategory.status ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => {
                                setFormCategory(subCategory);
                                setParentCategory(undefined);
                                setShowFormModal(true);
                              }}
                              className="w-8 h-8 flex items-center justify-center text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                              title="编辑"
                            >
                              <i className="ri-edit-line"></i>
                            </button>
                            <button
                              onClick={() => handleDelete(subCategory.id, false, (subCategory.productCount || 0) > 0)}
                              className="w-8 h-8 flex items-center justify-center text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="删除"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </>
                ))}
              </tbody>
            </table>
          </div>

          {filteredCategories.length === 0 && (
            <div className="text-center py-12">
              <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-gray-500">暂无分类数据</p>
            </div>
          )}
        </div>
      </div>

      {showFormModal && (
        <CategoryFormModal
          category={formCategory}
          parentCategory={parentCategory}
          onClose={() => {
            setShowFormModal(false);
            setFormCategory(undefined);
            setParentCategory(undefined);
          }}
          onSave={handleSaveForm}
        />
      )}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        type={confirmModal.type}
      />
    </MainLayout>
  );
}
